import { Component, ElementRef, ViewChild, ChangeDetectionStrategy, Input } from '@angular/core';
import { CHATBOT_IMG, CHATBOT_SCRIPTS, DEFAULT_SCRIPT_SRC, getAivoUser, PopupMessages } from './store/script.store';
import { AivoFn, ChatbotRelativePositionSettings, ScriptType } from './types';
import { SessionService } from '../../../../../../angular-services-v8/session.service';

@Component({
  selector: 'cmx-chatbot',
  styleUrls: ['chatbot.component.scss'],
  templateUrl: 'chatbot.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClass',
  }
})

export class ChatbotComponent {
  public readonly chatbotImg = CHATBOT_IMG;
  private language: string = '';
  public message = PopupMessages.en;
  public isInitilized = false;

  @ViewChild('cbpopup') private popupEl: ElementRef<HTMLDivElement>;
  @ViewChild('cbdock') private dockEl: ElementRef<HTMLDivElement>;
  @ViewChild('cbfloat') private containerEl: ElementRef<HTMLDivElement>;

  @Input() public relativeSettings: ChatbotRelativePositionSettings | null = null;
  @Input() public size = 56;
  @Input() public set languageISO(value: string) {
    if (value && value !== this.language) {
      this.language = value;
      this.initializeScript(value);
    }
  };

  public get isRelativePositionDisabled(): boolean {
    return this.relativeSettings?.enabled !== true;
  }

  get hostClass(): string {
    return 'cwc-chatbot' + (this.isRelativePositionDisabled ? ' right' : ' relative-mode');
  }

  constructor(private sessionService: SessionService) {
    if (!window.aivoFn) {
      window.aivoFn = {} as AivoFn;
    }
  }

  private initializeScript(languageISO: string): void {
    const isJwtPresent = !!this.sessionService.jwt;
    const scriptType = isJwtPresent ? '_private' : '_public';
    const scriptName = languageISO + scriptType;
    const matchedScript = CHATBOT_SCRIPTS.find(script => script.name === scriptName);
    const token = matchedScript?.src || DEFAULT_SCRIPT_SRC;
    this.loadOlivia(token, scriptType, languageISO);
  }

  private loadOlivia(token: string, scriptType: ScriptType, languageISO: string): void {
    const country = this.getCurrentCountry();
    this.message = languageISO === 'es' ? PopupMessages.es : PopupMessages.en;
    window.aivoFn = {
      // Initialization: Executes on the initial page load
      init: () => {
        const defaultToken = token;
        window.aivoFn.checkAndInsertMainScript(defaultToken);
      },
      // Function that handles the full cycle: removing previous scripts, validating, and loading the new script
      checkAndInsertMainScript: (defaultToken) => {
        // const src = 'https://agentcore-test.s3.amazonaws.com/production/cemex-2/core.js';
        const src = 'https://cdn.agentbot.net/core/latest/core.js';
        // Remove all old scripts and the bot container
        window.aivoFn.removeAllAgentbotScripts();
        window.aivoFn.removeAgentAppContainer(); // Remove the bot container
        // Insert the new script and execute the bot
        window.aivoFn.insertScript(src, defaultToken);
      },
      // Function that executes when the language changes or during navigation
      onNavigationChange: (defaultToken) => {
        window.aivoFn.checkAndInsertMainScript(defaultToken);
      },
      // Function to insert a script into the DOM and ensure it runs correctly
      insertScript: (src, defaultToken) => {
        const script = document.createElement('script');
        script.type = 'application/javascript';
        script.src = src;
        script.async = true;
        // Add a listener to ensure the script runs when it's loaded
        script.onload = () => {
          window.aivoFn.runBot(defaultToken);
        };
        script.onerror = function () {
          console.error(`Error loading script ${src}`);
        };
        document.body.appendChild(script);
      },
      // Function that runs the bot after the script has loaded
      runBot: (defaultToken) => {
        // Wait until $aivo is ready before running the bot
        const waitForAivo = setInterval(() => {
          if (!window.$aivo) {
            clearInterval(waitForAivo);
            window.aivoFn.init();
            return;
          }
          window.$aivo.status = undefined;
          if (window.$aivo && window.$aivo.run) {
            clearInterval(waitForAivo); // Stop waiting once $aivo is available
            window.$aivo.run(defaultToken); // Run the bot with the provided token
            // Open the chat window
            window.$aivo.ready(() => {
              this.isInitilized = true;
              const rfc = this.sessionService.currentLegalEntityValue?.legalEntityDesc;
              const aivoUser = getAivoUser(scriptType, country, this.sessionService.userProfile, rfc);
              window.$aivo.user.set(aivoUser);
              this.registerChatListeners();
            });
          }
        }, 500); // Check every 500ms if $aivo is ready
      },
      // Function to remove all scripts related to "agentbot"
      removeAllAgentbotScripts: () => {
        this.isInitilized = false;
        const scripts = document.querySelectorAll('script[src*="agentbot"]');
        scripts.forEach((script) => script.remove());
      },
      // Function to remove the AgentBot container from the DOM
      removeAgentAppContainer: () => {
        const container = document.getElementById('AgentAppContainer');
        container?.remove(); // Remove the bot container
      },
      // Function to check if the main script is already loaded
      isScriptLoaded: (src) => !!document.querySelector(`script[src="${src}"]`),
      isChatOpen: false
    };
    window.aivoFn.init();
  }

  public onCloseButton() {
    this.dockEl?.nativeElement.classList.add('active');
    this.containerEl?.nativeElement.classList.add('hidden');
    this.popupEl?.nativeElement.classList.remove('hidden');
    setTimeout(() => this.popupEl?.nativeElement.classList.add('hidden'), 2000);
  }

  public onDockButton() {
    this.dockEl?.nativeElement.classList.remove('active');
    this.containerEl?.nativeElement.classList.remove('hidden');
    this.popupEl?.nativeElement.classList.add('hidden');
  }

  public onToogleChat() {
    window.$aivo.chat[window.aivoFn.isChatOpen ? 'minimize' : 'open']();
  }

  public onPopupClick() {
    this.popupEl?.nativeElement.classList.add('hidden');
    window.$aivo.chat.open();
  }

  private registerChatListeners(): void {
    window.$aivo.chat.onCloseWindow(() => {
      window.aivoFn.isChatOpen = false;
    });
    window.$aivo.chat.onOpenWindow(() => {
      window.aivoFn.isChatOpen = true;
    });
    window.$aivo.chat.onMinimizeWindow(() => {
      window.aivoFn.isChatOpen = false;
    });
  }

  private getCurrentCountry() {
    let positionOfUnderscore: number;
    let currentCountry: string;
    if (sessionStorage.getItem('language') !== null) {
      positionOfUnderscore = sessionStorage.getItem('language').indexOf('_') + 1;
      currentCountry = sessionStorage.getItem('language').substring(positionOfUnderscore);
    } else if (localStorage.getItem('language') !== null) {
      positionOfUnderscore = localStorage.getItem('language').indexOf('_') + 1;
      currentCountry = localStorage.getItem('language').substring(positionOfUnderscore);
    } else if (window['LANGUAGE'] !== null) {
      positionOfUnderscore = window['LANGUAGE'].indexOf('_') + 1;
      currentCountry = window['LANGUAGE'].substring(positionOfUnderscore);
    }
    return currentCountry;
  }
}
